/* eslint @typescript-eslint/no-explicit-any: 0 */

import { clearAllBodyScrollLocks, enableBodyScroll } from 'body-scroll-lock';
import qs from 'qs';
import { useEffect, useRef, useState } from 'react';
import {
  Configure,
  connectStateResults,
  SearchBox
} from 'react-instantsearch-dom';

import RelatedSearchClickEvent from '~/events/RelatedSearchClickEvent';
import SearchInputClickEvent from '~/events/SearchInputClickEvent';
import useRootData from '~/hooks/useRootData';
import type {
  AllResultsType,
  AllSearchResultsType,
  SearchPanelProps,
  SearchPanelResultsProps,
  SearchSuggestionProps
} from '~/legacy/types';
import { bem, getAppRoot } from '~/legacy/utils';

import Icon from '../../Icon';
import StandardModal from '../../StandardModal';
import IfHits from './IfHits';
import InstantSearch, { Index } from './InstantSearch';
import SearchPanelHits from './SearchPanelHits';
import SearchPanelRefinedResults from './SearchPanelRefinedResults';
import SearchPanelResultsTypes from './SearchPanelResultsTypes';

const options = {
  clothing: {
    clickAnalytics: true,
    filters: 'type:product AND sub_type:clothing',
    hitsPerPage: 5
  },
  footwear: {
    clickAnalytics: true,
    filters: 'type:product AND sub_type:sneaker',
    hitsPerPage: 5
  },
  news: {
    clickAnalytics: true,
    filters: 'type:article',
    hitsPerPage: 5
  },
  suggestion: {
    clickAnalytics: true,
    hitsPerPage: 7
  }
};

export const RelatedSearches = connectStateResults(({ searchResults }) => {
  return searchResults && searchResults.nbHits ? (
    <span onClick={() => {}} className="search-panel__suggestions-message">
      Related searches
    </span>
  ) : null;
});

const SearchSuggestion = ({
  hit: { query, objectID },
  onClick,
  searchTerm
}: SearchSuggestionProps) => {
  const { isBot } = useRootData();

  const handleRelatedSearchesClick = (e: React.MouseEvent<HTMLLIElement>) => {
    const eventData = {
      isBot,
      related_term: query,
      search_term: searchTerm
    };

    const event = new RelatedSearchClickEvent(eventData);
    event.send();

    onClick(e);
  };

  return (
    <li
      key={objectID}
      onClick={handleRelatedSearchesClick}
      className="btn btn--tag btn--dark"
    >
      {query}
    </li>
  );
};

const noResults = ({
  allSearchResults
}: {
  allSearchResults: AllSearchResultsType;
}) => {
  const {
    footwear: { nbHits: footwearHits } = { nbHits: 0 },
    news: { nbHits: newsHits } = { nbHits: 0 },
    clothing: { nbHits: clothingHits } = { nbHits: 0 }
  } = allSearchResults || {};

  return footwearHits === 0 && newsHits === 0 && clothingHits === 0;
};

const anyResults = ({
  allSearchResults
}: {
  allSearchResults: AllSearchResultsType;
  searchResults: any;
}) => {
  const {
    footwear: { nbHits: footwearHits } = { nbHits: 0 },
    news: { nbHits: newsHits } = { nbHits: 0 },
    clothing: { nbHits: clothingHits } = { nbHits: 0 }
  } = allSearchResults || {};

  return footwearHits > 0 || newsHits > 0 || clothingHits > 0;
};

export const AllResults = connectStateResults(
  ({ searchState, searchResults, setPreventEnterOnSearch }: AllResultsType) => {
    const results = searchResults && searchResults.nbHits !== 0;

    useEffect(() => {
      setPreventEnterOnSearch(!results);
    }, [results, setPreventEnterOnSearch]);

    return results ? null : (
      <div className="search-panel__noresults">
        <p className="search-panel__noresults-searchterm">
          No results for “{searchState.query}”
        </p>
      </div>
    );
  }
);

export const SearchPanelResults = ({
  close = () => {},
  setQuery = (query: any) => {},
  query,
  displayState,
  setDisplayState,
  setPreventEnterOnSearch,
  isLargeBreakpoint,
  isMediumBreakpoint
}: SearchPanelResultsProps) => {
  const searchLocationFootwear = {
    pathname: '/release-dates/',
    search: qs.stringify({ q: query })
  };
  const searchLocationNews = {
    pathname: '/news/',
    search: qs.stringify({ q: query })
  };

  const searchLocationClothing = {
    pathname: '/clothing/',
    search: qs.stringify({ q: query })
  };

  const redirectLinks = {
    clothing: searchLocationClothing,
    footwear: searchLocationFootwear,
    news: searchLocationNews
  };

  return (
    <div className="content-width flex lg:mt-6 mb-10 flex-col md:gap-6">
      <Index indexName="search_suggestions">
        <ul className="search-panel__suggestions -mx-4 px-4 no-scrollbar">
          <Configure {...options.suggestion} />
          <RelatedSearches />
          <SearchPanelHits
            hitComponent={(props: any) => (
              <SearchSuggestion {...props} searchTerm={query} />
            )}
            hitProps={{
              onClick: (e: any) => {
                setQuery(e.target.innerText || e.target.textContent);
              }
            }}
          />
        </ul>
      </Index>

      {!isMediumBreakpoint && !isLargeBreakpoint && (
        <ul className="search-panel__refine">
          <SearchPanelRefinedResults
            displayState={displayState}
            setDisplayState={setDisplayState}
            searchCondition={anyResults}
            redirectLinks={redirectLinks}
            searchOptions={options}
          />
        </ul>
      )}

      <IfHits condition={noResults}>
        <AllResults setPreventEnterOnSearch={setPreventEnterOnSearch} />
      </IfHits>

      <div className="search-panel__results">
        <SearchPanelResultsTypes
          redirectLinks={redirectLinks}
          searchOptions={options}
          isLargeBreakpoint={isLargeBreakpoint}
          isMediumBreakpoint={isMediumBreakpoint}
          displayState={displayState}
          close={close}
          query={query}
          searchCondition={anyResults}
        />
      </div>
    </div>
  );
};

const SearchPanel = ({
  searchPanelState,
  isLargeBreakpoint,
  isMediumBreakpoint
}: SearchPanelProps) => {
  const { isBot } = useRootData();
  const scrollTargetElement = getAppRoot();
  const [active, setActive] = searchPanelState;
  const [searchState, setSearchState] = useState({});
  const [displayState, setDisplayState] = useState('footwear');
  const [modifiers, setModifiers] = useState<string[]>([]);
  const [preventEnterOnSearch, setPreventEnterOnSearch] = useState(false);
  const linkRef = useRef<HTMLAnchorElement>();

  const setQuery = (query: any) => {
    setSearchState({ ...searchState, query });
  };

  const { query } = searchState as any;

  const searchPanelClosed = () => {
    setActive(false);
    setModifiers([
      isLargeBreakpoint ? 'large' : 'small',
      'primary',
      '',
      !isLargeBreakpoint ? 'increase-input-size' : ''
    ]);
    clearAllBodyScrollLocks();
    scrollTargetElement && enableBodyScroll(scrollTargetElement);
  };

  const handleSearchInteraction = () => {
    setActive(true);
    new SearchInputClickEvent({
      isBot
    }).send();
  };

  const searchBoxClicked = () => {
    setActive(true);
    setModifiers([
      isLargeBreakpoint ? 'large' : 'small',
      'primary',
      isLargeBreakpoint ? 'large-active' : '',
      !isLargeBreakpoint ? 'increase-input-size' : ''
    ]);
  };

  const resultsProps = {
    close: searchPanelClosed,
    displayState,
    query,
    setDisplayState,
    setPreventEnterOnSearch,
    setQuery
  };

  useEffect(() => {
    setModifiers([
      isLargeBreakpoint ? 'large' : 'small',
      'primary',
      isLargeBreakpoint && active ? 'large-active' : '',
      !isLargeBreakpoint ? 'increase-input-size' : ''
    ]);
  }, [isLargeBreakpoint, active]);

  return (
    <div className={bem('search-panel', modifiers)}>
      <InstantSearch
        searchState={searchState}
        onSearchStateChange={setSearchState}
        indexName="search_suggestions"
      >
        <div className={bem('search-panel__input', { active })}>
          <div className="search-panel__input-container content-width lg:px-0">
            <div onClick={searchBoxClicked} style={{ flex: 1 }}>
              <a
                href={`/release-dates/?q=${query}`}
                ref={linkRef as any}
                style={{ display: 'none' }}
              >
                Redirect to search query
              </a>
              <SearchBox
                autoFocus={active}
                className="search-panel__searchbox"
                translations={{ placeholder: 'Search' }}
                onFocus={handleSearchInteraction}
                reset={
                  <Icon
                    name="times-circle"
                    width="15"
                    height="15"
                    viewBox="0 0 1792 1792"
                    fill="#949495"
                    className="search-panel__ios-close"
                  />
                }
                submit={
                  <Icon
                    name="search"
                    width="15"
                    height="15"
                    viewBox="0 0 1792 1792"
                    fill="#B7B7B8"
                  />
                }
                onSubmit={(e) => {
                  e.preventDefault();
                  if (linkRef.current && query && !preventEnterOnSearch) {
                    linkRef.current.click();
                  }
                  searchPanelClosed();
                }}
              />
            </div>
            <div className="search-panel__close-modal lg:hidden">
              <p
                className="search-panel__close-modal-text hidden md:block"
                onClick={searchPanelClosed}
              >
                Close
              </p>

              <div className="md:hidden">
                <Icon
                  onClick={searchPanelClosed}
                  name="close"
                  width="24"
                  height="24"
                  viewBox="0 0 1792 1792"
                  fill="black"
                  className="search-panel__close-modal-icon"
                />
              </div>
            </div>
          </div>
        </div>

        {active && (
          <>
            {isLargeBreakpoint ? (
              <StandardModal
                onCloseClick={searchPanelClosed}
                isFullWidth
                preventBodyScrollLock={false}
                disableClickOutside={false}
                hideClose={false}
                isFullBody={false}
              >
                <SearchPanelResults
                  {...resultsProps}
                  isLargeBreakpoint={isLargeBreakpoint}
                  isMediumBreakpoint={isMediumBreakpoint}
                />
              </StandardModal>
            ) : (
              <SearchPanelResults
                {...resultsProps}
                isLargeBreakpoint={isLargeBreakpoint}
                isMediumBreakpoint={isMediumBreakpoint}
              />
            )}
          </>
        )}
      </InstantSearch>
    </div>
  );
};

export default SearchPanel;
